/**
 * useContentConfig Hook
 * Manages content generation configuration state and API interactions
 */

import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '@/utils/auth-fetch';
import { describeResponseError } from '@/utils/responseError';

// Types
export type TonePreset = 'professional' | 'casual' | 'technical' | 'friendly' | 'authoritative' | 'conversational';
export type Perspective = 'first-person-plural' | 'second-person' | 'third-person';
export type Formality = 'formal' | 'neutral' | 'informal';
export type ContentLength = 'brief' | 'standard' | 'detailed';
export type DescriptionLength = 'concise' | 'standard' | 'comprehensive';
export type SectionDepth = 'shallow' | 'standard' | 'deep';
export type PriceInclusion = 'yes' | 'no' | 'product_pages_only';
export type PriceFormat = 'exact' | 'range' | 'relative';
export type BrandFrequency = 'natural' | 'emphasized' | 'minimal';
export type CompetitorPolicy = 'never' | 'compare_favorably' | 'neutral';
export type CtaStyle = 'soft' | 'medium' | 'strong';
export type CtaPlacement = 'end_only' | 'throughout' | 'natural';
export type FaqBrandComparison = 'enabled' | 'disabled';
export type ContentType = 'category_aeo' | 'product_aeo' | 'faq' | 'ai_article' | 'product_description';
export type SizeMentionMode = 'include' | 'title_only' | 'exclude';
export type SizeMentionSection = 'faq' | 'benefits' | 'use_cases' | 'comparisons' | 'summary' | 'buyer_profiles' | 'recommendations';

export interface ToneConfig {
  preset?: TonePreset;
  customVoice?: string;
  perspective: Perspective;
  formality: Formality;
}

export interface LengthConfig {
  faqLength: ContentLength;
  descriptionLength: DescriptionLength;
  sectionDepth: SectionDepth;
  // task-7xwk286v2a — Jaume, 2026-08-24: "a number defined by the user (1-3, defaulting to 3)".
  // null = unset at this scope, inherit from the next-less-specific level (site -> category ->
  // content_type), same convention as every other nullable field ContentConfigService resolves.
  maxComparisonFaqs: number | null;
}

export interface PricingConfig {
  includePrices: PriceInclusion;
  priceFormat: PriceFormat;
  priceCurrency: string;
  mentionSales: boolean | null;
}

export interface BrandConfig {
  brandNameVariant?: string;
  brandFrequency: BrandFrequency;
  competitorPolicy: CompetitorPolicy;
  ctaStyle: CtaStyle;
  ctaPlacement: CtaPlacement;
  faqBrandComparison: FaqBrandComparison;
}

export interface LanguageConfig {
  primaryLanguage: string;
  regionalVariant?: string;
  units: 'metric' | 'imperial' | 'both';
}

export interface ProductDetailsConfig {
  sizeMentionMode?: SizeMentionMode | null;
  sizeMentionSections?: SizeMentionSection[] | null;
}

export interface ContentGenerationConfig {
  id?: string;
  siteId: string;
  scope: 'site' | 'category' | 'content_type';
  categoryPath?: string | null;
  contentType?: ContentType | null;
  tone: ToneConfig;
  length: LengthConfig;
  pricing: PricingConfig;
  brand: BrandConfig;
  language: LanguageConfig;
  productDetails?: ProductDetailsConfig;
  aeoLlmModel?: string; // LLM model for AEO generation
  enrichmentLlmModel?: string | null; // LLM model for ProductEnrichmentService
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ResolvedConfig {
  tone: ToneConfig;
  length: LengthConfig;
  pricing: PricingConfig;
  brand: BrandConfig;
  language: LanguageConfig;
  productDetails?: ProductDetailsConfig;
  aeoLlmModel?: string;
  enrichmentLlmModel?: string;
  sources: {
    tone: string;
    length: string;
    pricing: string;
    brand: string;
    language: string;
    productDetails?: string;
    aeoLlmModel?: string;
    enrichmentLlmModel?: string;
  };
}

export interface ConfigurationsResponse {
  site: ContentGenerationConfig | null;
  categories: ContentGenerationConfig[];
  contentTypes: ContentGenerationConfig[];
}

export interface RegenerationJob {
  id: string;
  siteId: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  totalItems: number;
  completedItems: number;
  failedItems: number;
  estimatedCost: number;
  actualCost: number;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  errors?: Array<{ contentId: string; contentType: string; error: string }>;
}

export interface RegenerationEstimate {
  jobId?: string;
  estimatedItems: number;
  estimatedCost: number;
  estimatedSeconds: number;
}

// Default config values
export const DEFAULT_CONFIG: Omit<ContentGenerationConfig, 'id' | 'siteId' | 'createdAt' | 'updatedAt'> = {
  scope: 'site',
  tone: {
    preset: 'professional',
    perspective: 'first-person-plural',
    formality: 'neutral',
  },
  length: {
    faqLength: 'standard',
    descriptionLength: 'standard',
    sectionDepth: 'standard',
    maxComparisonFaqs: 3,
  },
  pricing: {
    includePrices: 'product_pages_only',
    priceFormat: 'exact',
    priceCurrency: 'auto',
    mentionSales: null,
  },
  brand: {
    brandFrequency: 'natural',
    competitorPolicy: 'never',
    ctaStyle: 'medium',
    ctaPlacement: 'natural',
    faqBrandComparison: 'enabled',
  },
  language: {
    primaryLanguage: 'en',
    units: 'metric',
  },
  productDetails: {
    sizeMentionMode: null,
    sizeMentionSections: null,
  },
  aeoLlmModel: 'gemini-2.5-pro',
  enrichmentLlmModel: 'gpt-4o-mini',
  isActive: true,
};

interface UseContentConfigOptions {
  siteId: string | null | undefined;
}

export function useContentConfig({ siteId }: UseContentConfigOptions) {
  const [configurations, setConfigurations] = useState<ConfigurationsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Load configurations
  const loadConfigurations = useCallback(async () => {
    if (!siteId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(`/content-config/${siteId}`);
      if (!response.ok) {
        throw new Error('Failed to load configurations');
      }
      const result = await response.json();
      // API returns { success: true, data: {...} } - extract the data
      setConfigurations(result.data || result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      console.error('[useContentConfig] Error loading configurations:', err);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  // Save configuration
  const saveConfiguration = useCallback(async (config: Partial<ContentGenerationConfig>) => {
    if (!siteId) throw new Error('Site ID required');

    setSaving(true);
    setError(null);

    try {
      const response = await authFetch(`/content-config/${siteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        // No silent fallbacks (task-9po368xgoh). The previous form was
        // `response.json().catch(() => ({}))` + `errorData.error || 'Failed to save
        // configuration'`, which DISCARDED any non-JSON error body and dropped the
        // HTTP status with it — which is why the CEO-reported save failure arrived
        // carrying no diagnostic at all. See utils/responseError.ts.
        throw new Error(await describeResponseError(response, 'Save failed'));
      }

      const saved = await response.json();

      // Reload configurations to get updated data
      await loadConfigurations();

      return saved;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [siteId, loadConfigurations]);

  // Delete configuration
  const deleteConfiguration = useCallback(async (configId: string) => {
    if (!siteId) throw new Error('Site ID required');

    try {
      const response = await authFetch(`/content-config/${siteId}/${configId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete configuration');
      }

      // Reload configurations
      await loadConfigurations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    }
  }, [siteId, loadConfigurations]);

  // Resolve configuration for a specific context
  const resolveConfig = useCallback(async (
    categoryPath?: string | null,
    contentType?: ContentType | null
  ): Promise<ResolvedConfig | null> => {
    if (!siteId) return null;

    try {
      const params = new URLSearchParams();
      if (categoryPath) params.set('categoryPath', categoryPath);
      if (contentType) params.set('contentType', contentType);

      const response = await authFetch(`/content-config/${siteId}/resolve?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to resolve configuration');
      }

      return await response.json();
    } catch (err) {
      console.error('[useContentConfig] Error resolving config:', err);
      return null;
    }
  }, [siteId]);

  // Generate preview
  const generatePreview = useCallback(async (
    categoryPath: string,
    contentType: ContentType,
    currentConfig?: {
      tone: ToneConfig;
      length: LengthConfig;
      pricing: PricingConfig;
      brand: BrandConfig;
      language: LanguageConfig;
    }
  ) => {
    if (!siteId) throw new Error('Site ID required');

    try {
      const response = await authFetch(`/content-config/${siteId}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoryPath, contentType, currentConfig }),
      });

      if (!response.ok) {
        // Paired with the handleGeneratePreview catch in ContentConfigPage. Surfacing
        // the error in the page while this still threw one fixed string for every
        // failure would have been a change that LOOKS like a fix and shows the same
        // generic text — the exact shape of the defect being fixed.
        throw new Error(await describeResponseError(response, 'Preview failed'));
      }

      return await response.json();
    } catch (err) {
      console.error('[useContentConfig] Error generating preview:', err);
      throw err;
    }
  }, [siteId]);

  // Estimate regeneration
  const estimateRegeneration = useCallback(async (
    categoryPath?: string,
    contentTypes: ContentType[] = ['category_aeo', 'product_aeo', 'faq']
  ): Promise<RegenerationEstimate | null> => {
    if (!siteId) return null;

    try {
      const response = await authFetch(`/content-config/${siteId}/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoryPath, contentTypes, dryRun: true }),
      });

      if (!response.ok) {
        throw new Error('Failed to estimate regeneration');
      }

      return await response.json();
    } catch (err) {
      console.error('[useContentConfig] Error estimating regeneration:', err);
      return null;
    }
  }, [siteId]);

  // Start regeneration job
  const startRegeneration = useCallback(async (
    categoryPath?: string,
    contentTypes: ContentType[] = ['category_aeo', 'product_aeo', 'faq']
  ): Promise<RegenerationEstimate | null> => {
    if (!siteId) throw new Error('Site ID required');

    try {
      const response = await authFetch(`/content-config/${siteId}/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoryPath, contentTypes, dryRun: false }),
      });

      if (!response.ok) {
        throw new Error('Failed to start regeneration');
      }

      return await response.json();
    } catch (err) {
      console.error('[useContentConfig] Error starting regeneration:', err);
      throw err;
    }
  }, [siteId]);

  // Get regeneration job status
  const getJobStatus = useCallback(async (jobId: string): Promise<RegenerationJob | null> => {
    if (!siteId) return null;

    try {
      const response = await authFetch(`/content-config/${siteId}/regenerate/${jobId}`);
      if (!response.ok) {
        throw new Error('Failed to get job status');
      }

      return await response.json();
    } catch (err) {
      console.error('[useContentConfig] Error getting job status:', err);
      return null;
    }
  }, [siteId]);

  // Cancel regeneration job
  const cancelJob = useCallback(async (jobId: string) => {
    if (!siteId) throw new Error('Site ID required');

    try {
      const response = await authFetch(`/content-config/${siteId}/regenerate/${jobId}/cancel`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to cancel job');
      }

      return await response.json();
    } catch (err) {
      console.error('[useContentConfig] Error cancelling job:', err);
      throw err;
    }
  }, [siteId]);

  // Load configurations on mount
  useEffect(() => {
    if (siteId) {
      loadConfigurations();
    }
  }, [siteId, loadConfigurations]);

  return {
    // State
    configurations,
    loading,
    error,
    saving,

    // Actions
    loadConfigurations,
    saveConfiguration,
    deleteConfiguration,
    resolveConfig,
    generatePreview,
    estimateRegeneration,
    startRegeneration,
    getJobStatus,
    cancelJob,
  };
}
